/* eslint-disable no-console */
const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY;
const FUNCTION_URL = `${SUPABASE_URL}/functions/v1/google-oauth`;

// Headers requeridos para llamar Edge Functions de Supabase
const getHeaders = () => ({
  Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
  "Content-Type": "application/json",
});

/**
 * Obtiene la URL de autorización de Google OAuth
 * @param {number} usuarioId - ID del usuario
 * @param {string} redirectUri - URL de redirección después de autorizar
 * @param {string} pais - Código de país (CHI, PER, URU)
 * @returns {Promise<string>} URL de autorización
 */
export const getGoogleAuthUrl = async (usuarioId, redirectUri, pais = "CHI") => {
  const url = new URL(FUNCTION_URL);
  url.searchParams.set("action", "authorize");
  url.searchParams.set("usuario_id", usuarioId.toString());
  url.searchParams.set("pais", pais);
  if (redirectUri) url.searchParams.set("redirect_uri", redirectUri);

  const response = await fetch(url.toString(), { headers: getHeaders() });
  const data = await response.json();

  if (data.error) throw new Error(data.error);
  return data.auth_url;
};

/**
 * Verifica el estado de conexión de Google para un usuario
 * @param {number} usuarioId - ID del usuario
 * @returns {Promise<{connected: boolean, expired?: boolean, scopes?: string[], last_updated?: string}>}
 */
export const getGoogleConnectionStatus = async (usuarioId) => {
  const url = new URL(FUNCTION_URL);
  url.searchParams.set("action", "status");
  url.searchParams.set("usuario_id", usuarioId.toString());

  const response = await fetch(url.toString(), { headers: getHeaders() });
  return response.json();
};

/**
 * Desconecta Google OAuth para un usuario
 * @param {number} usuarioId - ID del usuario
 * @returns {Promise<{success: boolean}>}
 */
export const disconnectGoogle = async (usuarioId) => {
  const url = new URL(FUNCTION_URL);
  url.searchParams.set("action", "disconnect");
  url.searchParams.set("usuario_id", usuarioId.toString());

  const response = await fetch(url.toString(), { headers: getHeaders() });
  return response.json();
};

/**
 * Inicia el flujo de conexión con Google
 * @param {number} usuarioId - ID del usuario
 * @param {string} redirectUri - URL de redirección
 * @param {string} pais - Código de país (CHI, PER, URU)
 */
export const connectGoogle = async (usuarioId, redirectUri, pais = "CHI") => {
  const authUrl = await getGoogleAuthUrl(usuarioId, redirectUri, pais);
  if (!authUrl) {
    throw new Error("No se recibió URL de autorización de Google");
  }
  window.location.href = authUrl;
};

/**
 * Sincroniza correos de Gmail como actividades CRM
 * @param {number} usuarioId - ID del usuario
 * @param {string} pais - Código de país (CHI, PER, etc.)
 * @param {number} maxResults - Cantidad máxima de correos a sincronizar
 * @returns {Promise<{success: boolean, synced: number, activities: Array}>}
 */
export const syncGmail = async (usuarioId, pais = "CHI", maxResults = 50) => {
  const url = new URL(`${SUPABASE_URL}/functions/v1/gmail-sync`);
  url.searchParams.set("usuario_id", usuarioId.toString());
  url.searchParams.set("pais", pais);
  url.searchParams.set("max_results", maxResults.toString());

  const response = await fetch(url.toString(), { headers: getHeaders() });
  const data = await response.json();

  if (data.error) throw new Error(data.error);
  return data;
};

/**
 * Sincroniza eventos de Calendar como actividades CRM
 * @param {number} usuarioId - ID del usuario
 * @param {string} pais - Código de país (CHI, PER, etc.)
 * @returns {Promise<{success: boolean, synced: number, activities: Array}>}
 */
export const syncCalendar = async (usuarioId, pais = "CHI") => {
  const url = new URL(`${SUPABASE_URL}/functions/v1/calendar-sync`);
  url.searchParams.set("usuario_id", usuarioId.toString());
  url.searchParams.set("pais", pais);

  const response = await fetch(url.toString(), { headers: getHeaders() });
  const data = await response.json();

  if (data.error) throw new Error(data.error);
  return data;
};

// Caller compartido de la edge function calendar-events (create/list/update/delete).
// Propaga .code (NEEDS_RECONNECT / NO_TOKEN / ...) en el Error para que el front decida.
const callCalendarEvents = async (action, payload, fallbackMsg) => {
  const url = `${SUPABASE_URL}/functions/v1/calendar-events`;
  const response = await fetch(url, {
    method: "POST",
    headers: getHeaders(),
    body: JSON.stringify({ action, ...payload }),
  });
  const data = await response.json();
  if (!response.ok || data.error) {
    const err = new Error(data.error || fallbackMsg);
    if (data.code) err.code = data.code;
    throw err;
  }
  return data;
};

/**
 * Crea un evento en el Google Calendar (primary) del usuario logueado.
 * payload: { usuario_id, pais (CHI/PER), client_id, lado, tipo, tipo_label,
 *            cliente_nombre, fecha_inicio (ISO), nota? }
 * @returns {Promise<{success: boolean, event: Object}>}
 * @throws {Error} con .code = "NEEDS_RECONNECT" si falta scope de escritura
 */
export const createCalendarEvent = (payload) => callCalendarEvents("create", payload, "Error creando el evento");

/**
 * Lista los eventos wecast_crm futuros (próximos 90 días) del usuario logueado.
 * payload: { usuario_id, pais }
 * @returns {Promise<{success: boolean, events: Array<{id, summary, start, end, description, private}>}>}
 */
export const listCalendarEvents = (payload) => callCalendarEvents("list", payload, "Error listando eventos");

/**
 * Edita un evento por event_id, preservando su asociación (extendedProperties).
 * payload: { usuario_id, pais, event_id, tipo, tipo_label, cliente_nombre, fecha_inicio, nota? }
 * @returns {Promise<{success: boolean, event: Object}>}
 */
export const updateCalendarEvent = (payload) => callCalendarEvents("update", payload, "Error editando el evento");

/**
 * Borra un evento por event_id (sin confirmación del lado del servidor).
 * payload: { usuario_id, pais, event_id }
 * @returns {Promise<{success: boolean}>}
 */
export const deleteCalendarEvent = (payload) => callCalendarEvents("delete", payload, "Error borrando el evento");
